import { auth } from '../firebase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Helper to get auth token
const getAuthToken = async () => {
  const currentUser = auth.currentUser;
  if (currentUser) {
    return currentUser.getIdToken();
  }
  return null;
};

// Helper to make authenticated API requests
const fetchWithAuth = async (endpoint, options = {}) => {
  try {
    const token = await getAuthToken();
    
    const defaultOptions = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    };

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...(options.headers || {}),
      },
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `API request failed: ${response.status}`);
    }

    return data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    throw error;
  }
};

// Credits API
export const creditsApi = {
  // Get user's credits
  getCredits: async () => {
    return fetchWithAuth('/auth/credits');
  },
  
  // Deduct credits
  deductCredits: async (amount) => {
    return fetchWithAuth('/credits/deduct', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  },
  
  // Add credits (for admin or internal use)
  addCredits: async (amount) => {
    return fetchWithAuth('/credits/add', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    });
  },
};

// Progress API for real-time updates
export const progressApi = {
  // Create an SSE connection to receive real-time progress updates
  connectToProgressStream: async (onProgress, onComplete, onError) => {
    try {
      // Get the auth token
      const token = await getAuthToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Create EventSource with auth token in the URL
      // URL encoded to avoid issues with special characters
      const eventSourceUrl = `${API_URL}/progress/stream?token=${encodeURIComponent(token)}`;
      const eventSource = new EventSource(eventSourceUrl);
      
      // Listen for progress events
      eventSource.addEventListener('progress', (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onProgress) onProgress(data);
        } catch (error) {
          console.error('Error parsing progress data:', error);
        }
      });
      
      // Listen for completion events
      eventSource.addEventListener('complete', (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onComplete) onComplete(data);
        } catch (error) {
          console.error('Error parsing completion data:', error);
        } finally {
          eventSource.close();
        }
      });
      
      // Listen for connection events
      eventSource.addEventListener('connect', (event) => {
        console.log('Connected to progress stream');
      });
      
      // Handle errors
      eventSource.addEventListener('error', (event) => {
        console.error('SSE Error:', event);
        if (onError) onError(event);
        eventSource.close();
      });
      
      // Return the event source so it can be closed later
      return eventSource;
    } catch (error) {
      console.error('Failed to connect to progress stream:', error);
      if (onError) onError(error);
      return null;
    }
  }
};

// Humanize API
export const humanizeApi = {
  // Check if text is eligible for free re-humanization
  checkEligibility: async (content) => {
    return fetchWithAuth('/humanize/check-eligibility', {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },
  
  // Humanize content with real-time progress updates
  humanizeContent: async (content, mode = 'standard') => {
    return fetchWithAuth('/humanize', {
      method: 'POST',
      body: JSON.stringify({ content, mode }),
    });
  }
};

// Analytics API
export const analyticsApi = {
  // Get user usage statistics
  getStats: async () => {
    return fetchWithAuth('/analytics/stats');
  },
  
  // Get usage history
  getHistory: async (page = 1, limit = 10) => {
    return fetchWithAuth(`/analytics/history?page=${page}&limit=${limit}`);
  }
};

// Auth API
export const authApi = {
  // Get current user data with credits
  getCurrentUser: async () => {
    return fetchWithAuth('/auth/me');
  },
  
  // Register user in the backend after Firebase auth
  registerUser: async (displayName) => {
    return fetchWithAuth('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ displayName }),
    });
  },
};

// Admin API
export const adminApi = {
  // Get all users (admin only)
  getAllUsers: async () => {
    return fetchWithAuth('/admin/users');
  },
  
  // Get specific user details (admin only)
  getUserDetails: async (userId) => {
    return fetchWithAuth(`/admin/users/${userId}`);
  },
  
  // Adjust user credits (add or subtract) (admin only)
  adjustUserCredits: async (userId, amount) => {
    return fetchWithAuth('/admin/credits/adjust', {
      method: 'POST',
      body: JSON.stringify({ userId, amount }),
    });
  },
  
  // Get user's usage statistics (admin only)
  getUserStats: async (userId) => {
    return fetchWithAuth(`/admin/analytics/${userId}`);
  },
  
  // Get user's usage history (admin only)
  getUserHistory: async (userId, page = 1, limit = 10) => {
    return fetchWithAuth(`/admin/analytics/${userId}/history?page=${page}&limit=${limit}`);
  },
  
  // Toggle user's admin status (super admin only)
  toggleAdminStatus: async (userId, isAdmin) => {
    return fetchWithAuth('/admin/users/toggle-admin', {
      method: 'POST',
      body: JSON.stringify({ userId, isAdmin }),
    });
  },
};
